import type { ColumnsType } from "antd/es/table";
import type { DefaultOptionType } from "antd/es/select";
import {
  ContactsTableDataResponseType,
  getColumnsParamsType,
} from "../../../Types/DatabaseTabTypes";
import * as Yup from "yup";
import { regexPatterns } from "../../../utils/regexUtil";
import { PlacementPositions } from "../../../enums/PlacementPositionsEnums";
import styles from "./ContactsTable.module.scss";
import { MdDelete, MdModeEdit } from "react-icons/md";
import { EllipsisOutlined } from "@ant-design/icons";
import { Popover } from "antd";
import StarIcon from "../../../assets/images/star-filled.svg";
import clsx from "clsx";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import { DatabaseSections } from "../../../enums/DatabaseEnums";
import { ArchiveId, ArchiveKeys } from "../../../enums/ArchiveActionKeys";
import { getTableSortOrder } from "../../../utils/getTableSortOrder";

export const getColumns = ({
  sortBy,
  sortOrder,
  section,
  ...params
}: getColumnsParamsType): ColumnsType<ContactsTableDataResponseType> => {
  const isCustomer = section === DatabaseSections.CUSTOMER;

  return [
    {
      title: "CONTACT NAME",
      key: "contactName",
      align: "center",
      dataIndex: "contactName",
      sorter: isCustomer,
      sortOrder: isCustomer
        ? getTableSortOrder(sortBy, sortOrder, "contactName")
        : undefined,
      render: (_, record) => (
        <span className="table-font-mobile">
          {record?.contactName}
          <ConditionalRender visible={record?.isPrimary}>
            <img
              className={styles.primaryIndicator}
              src={StarIcon}
              alt={"primary"}
            />
          </ConditionalRender>
        </span>
      ),
    },
    {
      title: "ROLE",
      key: "contactRole",
      align: "center",
      dataIndex: "contactRole",
      sorter: isCustomer,
      sortOrder: isCustomer
        ? getTableSortOrder(sortBy, sortOrder, "contactRole")
        : undefined,
      render: (val) => <span className="table-font-mobile">{val}</span>,
    },
  {
    title: "PHONE",
    key: "phone",
    align: "center",
    dataIndex: "phone",
    render: (val) => <span className="table-font-mobile">{val}</span>,
  },
  {
    title: "EMAIL",
    key: "email",
    align: "center",
    dataIndex: "email",
    render: (val) => (
      <span className={clsx("table-font-mobile", styles.documentColor)}>
        {val}
      </span>
    ),
  },
  {
    title: "NOTE",
    key: "notes",
    align: "center",
    dataIndex: "notes",
    render: (val) => <span className="table-font-mobile">{val}</span>,
  },
  {
    title: "",
    key: "actions",
    dataIndex: "actions",
    fixed: "right",
    align: "right",
    render: (_, record) => {
      return (
      <ConditionalRender
        visible={!!!params?.blockedJob}
      >
        <Popover
          placement={PlacementPositions.LEFT}
          content={
            <>
              <div
                className={styles.PopoverContent}
                onClick={() => params?.handleEditNavigate(record?.id)}
              >
                <MdModeEdit />
                Edit
              </div>
              <div
                className={styles.PopoverContent}
                onClick={() => params?.handleDeleteModalOpen(record?.id)}
              >
                <MdDelete />
                Delete
              </div>
              <ConditionalRender visible={params?.isQuarry}>
                <ConditionalRender visible={!!!record?.isPrimary}>
                  <div
                    className={styles.PopoverContent}
                    onClick={() => params?.handleSetPrimary(record, true)}
                  >
                    <img src={StarIcon} alt={""} />
                    Make Primary
                  </div>
                </ConditionalRender>
                <ConditionalRender visible={!!record?.isPrimary}>
                  <div
                    className={styles.PopoverContent}
                    onClick={() => params?.handleSetPrimary(record, false)}
                  >
                    <img src={StarIcon} alt={""} />
                    Remove Primary
                  </div>
                </ConditionalRender>
              </ConditionalRender>
            </>
          }
        >
          <EllipsisOutlined
            style={{ padding: "0.5rem", fontSize: "30px", color: "#C3C8CD" }}
            className="action-ellipsis-button"
          />
        </Popover>
      </ConditionalRender>
      );
    },
  },
];
};

export const ContactsTableConstants = {
  SEARCH: "Search",
  BUTTON: {
    ADD_CONTACT_LABEL: "+ Add Contact",
  },
  MODAL: {
    ADD_CONTACT_MODAL_TITLE: "Add Contact",
    DELETE_CONTACT_MODAL_TITLE: "Delete Contact",
    SUBMIT_BUTTON_LABEL: "Submit",
    CONFIRM_BUTTON_LABEL: "Confirm",
    DELETE_MODAL_CONTENT:
      "This contact will be permanently deleted. Are you sure you want to proceed?",
    WIDTH: "716px",
    EDIT_CONTACT_MODAL_TITLE: "Edit Contact",
    ZINDEX: "1000",
  },
  VALIDATION_ERRORS: {
    ATLEAST_ONE_CONTACT_ERROR:
      "Atleast one contatct (EMAIL/PHONE) should present!",
    FAILED_TO_ADD_CONTACT: "Failed to add contact!",
    FAILED_TO_DELETE_CONTACT: "Failed to delete contact!",
    FAILED_TO_UPDATE_CONTACT: "Failed to update contact!",
    EMAIL_ALREADY_EXISTS: [
      "email already added in primary contact",
      "email already exists!",
      "phone already exists!",
    ],
  },
  SUCCESS_MESSAGES: {
    ADD_NEW_CONTACT_MESSAGE: "Successfully added new contact!",
    DELETE_CONTACT_MESSAGE: "Contact deleted successfully!",
    EDIT_CONTACT_MESSAGE: "Contact updated successfully!",
  },
};

export const addContactValidationSchema = Yup.object()
  .shape({
    firstName: Yup.string()
      .required("First name is required")
      .matches(regexPatterns.alphabetsOnlyRegex, "Must be only alphabets"),

    lastName: Yup.string()
      .required("Last name is required")
      .matches(regexPatterns.alphabetsOnlyRegex, "Must be only alphabets"),
    email: Yup.string()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .notRequired()
      .email("Enter valid email address"),
    phone: Yup.string()
      .transform((value) => {
        if (value === "") return null;
        return value ? value.replace(/[^\d]/g, "") : value;
      })
      .notRequired()
      .min(10, "Phone number must be 10 digits!"),
  })
  .test(
    "one-contact-method",
    "Either phone number or email is required",
    function (value) {
      const { email, phone } = value || {};
      if (email || phone) {
        return true;
      }
      return this.createError({
        path: "phone",
        message: "Either phone number or email, one is required",
      });
    },
  );

export const addRoleValidation = Yup.object().shape({
  roleName: Yup.string().required("Role name is required"),
});

export const addContactFields = {
  firstName: "firstName",
  lastName: "lastName",
  email: "email",
  phone: "phone",
  roleId: "roleId",
};

export const contactsDeletedTableMap = {
  [DatabaseSections.QUARRY]: ArchiveKeys.QUARRY_CONTACTS,
  [DatabaseSections.JOBS]: ArchiveKeys.CONTACTS,
  [DatabaseSections.CUSTOMER]: ArchiveKeys.CONTACTS,
};

export const contactsIdMap = {
  [DatabaseSections.QUARRY]: ArchiveId.QUARRY_ID,
};
